import React, { useState, useMemo, useEffect, useRef } from "react";
import { Select, Input, Button, Space, Card, Spin, Tag } from 'antd';
import OperationMenu from "./OperationMenu.jsx";
import OperationItem from "./OperationItem.jsx";
import QueryPreview from "./QueryPreview.jsx";
import rangeData from "../locales/rangeOptions.json";
import { alertApi, normalizeLokiLabelsResponse } from '../services/alertApi';
import { parseLogqlExpression } from '../utils/parseLogqlExpression';

const { Option } = Select;

<style>
{`
  /* Wrap selected item text */
  .loki-select .ant-select-selection-item {
    white-space: normal !important;
    word-break: break-all !important;
    color: black !important;
  }

  /* Wrap dropdown items */
  .loki-select .ant-select-item-option-content {
    white-space: normal !important;
    word-break: break-all !important;
    color: black !important;
  }

  /* Make placeholder black */
  .loki-select .ant-select-selection-placeholder {
    color: black !important;
  }
`}
</style>

export default function LokiBuilder(props) {
  const {
    labelKeys: propsLabelKeys,
    valueOptions: propsValueOptions,
    onChange,
    value,
    isDark = false,
    clientId,
    awsAccountId,
  } = props;
  const normalizeExpr = (input) => {
    let s = String(input || '').replace(/\s+/g, '');
    while (s.startsWith('(') && s.endsWith(')')) {
      let depth = 0;
      let fullyWrapped = true;
      for (let i = 0; i < s.length; i += 1) {
        if (s[i] === '(') depth += 1;
        else if (s[i] === ')') {
          depth -= 1;
          if (depth === 0 && i < s.length - 1) {
            fullyWrapped = false;
            break;
          }
        }
      }
      if (!fullyWrapped) break;
      s = s.slice(1, -1);
    }
    return s;
  };

  const [labels, setLabels] = useState([{ key: '', op: '=', value: '' }]);
  const [lineFilters, setLineFilters] = useState([]);
  const [ops, setOps] = useState([]);
  const [availableLabels, setAvailableLabels] = useState([]);
  const [availableValues, setAvailableValues] = useState({});
  const [loading, setLoading] = useState(
    !(propsLabelKeys && propsLabelKeys.length > 0)
  );
  const lastParsedValue = useRef(null);

  // When editing: parse initial expression to pre-fill labels, line filters, and operations
  useEffect(() => {
    if (!value || typeof value !== 'string' || value.trim() === '' || value === '{}') {
      lastParsedValue.current = null;
      return;
    }
    if (lastParsedValue.current === value) return;

    const parsed = parseLogqlExpression(value.trim());
    setLabels(
      parsed.labels?.length
        ? parsed.labels
        : [{ key: '', op: '=', value: '' }]
    );
    setLineFilters(parsed.lineFilters || []);
    setOps(parsed.ops || []);
    lastParsedValue.current = value;
  }, [value]);

  useEffect(() => {
    if (propsLabelKeys && propsLabelKeys.length > 0) {
      setLoading(false);
      return;
    }

    const applyLabels = (logsData) => {
      const { keys, options } = normalizeLokiLabelsResponse(logsData);
      if (keys.length > 0) {
        setAvailableLabels(keys);
        setAvailableValues(options);
      }
    };

    const logParams = { clientId, awsAccountId };

    const fetchLokiLabels = async () => {
      setLoading(true);
      try {
        const keysData = await alertApi.getAlertPageLogs({
          keysOnly: true,
          timeoutMs: 15000,
          ...logParams,
        });
        applyLabels(keysData);

        try {
          const fullData = await alertApi.getAlertPageLogs({
            keysOnly: false,
            timeoutMs: 60000,
            ...logParams,
          });
          applyLabels(fullData);
        } catch (valuesErr) {
          console.warn('Loki label values unavailable; label names loaded:', valuesErr);
        }
      } catch (error) {
        console.error('Failed to load Loki labels:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLokiLabels();
  }, [propsLabelKeys, clientId, awsAccountId]);

// Merge fetched labels with props (props take priority), sort alphabetically
const labelKeys = useMemo(() => {
  const keys = propsLabelKeys && propsLabelKeys.length > 0 
    ? propsLabelKeys 
    : availableLabels;
  return [...keys].sort((a, b) => String(a).localeCompare(String(b)));
}, [propsLabelKeys, availableLabels]);

const valueOptions = useMemo(() => {
  const raw = propsValueOptions && Object.keys(propsValueOptions).length > 0
    ? propsValueOptions
    : availableValues;
  const sorted = {};
  Object.keys(raw || {}).sort((a, b) => a.localeCompare(b)).forEach(k => {
    sorted[k] = [...(raw[k] || [])].sort((a, b) => String(a).localeCompare(String(b)));
  });
  return sorted;
}, [propsValueOptions, availableValues]);
  const rangeOptions = rangeData.ranges || ["$__auto"];

  function addOp(opSpec) {
    const base = {
      id: opSpec.id,
      display: opSpec.display || opSpec.id,
      params: {},
    };
    if (opSpec.needsRange) base.params.range = rangeOptions[0];
    if (opSpec.needsLabel) base.params.by = [];
    if (opSpec.input) base.input = opSpec.input;
    setOps((p) => [...p, base]);
  }

  function removeOp(i) {
    setOps((p) => p.filter((_, idx) => idx !== i));
  }
  function changeOpType(i, newOp) {
    setOps((p) => p.map((op, idx) => (idx === i ? { ...newOp } : op)));
  }
  function updateOpParam(i, field, val) {
    setOps((p) =>
      p.map((op, idx) =>
        idx === i ? { ...op, params: { ...op.params, [field]: val } } : op
      )
    );
  }

  function addLabel() {
    setLabels((p) => [...p, { key: "", op: "=", value: "" }]);
  }
  function updateLabel(i, field, val) {
    setLabels((p) =>
      p.map((f, idx) => (idx === i ? { ...f, [field]: val } : f))
    );
  }
  function removeLabel(i) {
    setLabels((p) => p.filter((_, idx) => idx !== i));
  }

  function addLine() {
    setLineFilters((p) => [...p, { type: "|=", value: "" }]);
  }
  function updateLine(i, field, val) {
    setLineFilters((p) =>
      p.map((l, idx) => (idx === i ? { ...l, [field]: val } : l))
    );
  }
  function removeLine(i) {
    setLineFilters((p) => p.filter((_, idx) => idx !== i));
  }

const query = useMemo(() => {
    // Build base
    const labelPairs = labels.filter(l => l.key && l.value).map(l => `${l.key}${l.op}"${l.value}"`);
    const selector = `{${labelPairs.join(",")}}`;
    const lineExpr = lineFilters.filter(l => l.value).map(l => ` ${l.type} "${l.value}"`).join("");
    let expr = selector + lineExpr;

    // ⭐ NEW: Categorize operations by type (this is critical for LogQL syntax!)
    const formatOps = [];       // Pipeline operations (json, logfmt, etc.)
    const lineFilterOps = [];   // Line filter operations
    const labelFilterOps = [];  // Label filter operations
    const unwrapOps = [];       // Unwrap operations
    const rangeOps = [];        // Range aggregation (rate, count_over_time, etc.)
    const aggregationOps = [];  // Aggregations (sum, avg, etc.)
    const binaryOps = [];       // Binary operations

    // Categorize each operation
    for (const o of ops) {
        const opId = o.id;
        
        if (['json', 'logfmt', 'regexp', 'pattern', 'unpack', 'decolorize', 
             'line_format', 'label_format', 'drop', 'keep'].includes(opId)) {
            formatOps.push(o);
        } else if (['line_contains', 'line_contains_not', 'line_contains_ci', 
                    'line_contains_not_ci', 'line_matches_regex', 'line_matches_regex_not', 
                    'line_filter_ip_matches'].includes(opId)) {
            lineFilterOps.push(o);
        } else if (['label_filter', 'label_filter_ip_matches', 
                    'label_filter_no_errors'].includes(opId)) {
            labelFilterOps.push(o);
        } else if (['unwrap', 'unwrap_duration', 'unwrap_bytes'].includes(opId)) {
            unwrapOps.push(o);
        } else if (['rate', 'rate_counter', 'count_over_time', 'sum_over_time', 
                    'avg_over_time', 'min_over_time', 'max_over_time', 'first_over_time', 
                    'last_over_time', 'stddev_over_time', 'stdvar_over_time', 
                    'quantile_over_time', 'bytes_rate', 'bytes_over_time', 
                    'absent_over_time'].includes(opId)) {
            rangeOps.push(o);
        } else if (['sum', 'avg', 'min', 'max', 'count', 'stddev', 'stdvar', 
                    'topk', 'bottomk'].includes(opId)) {
            aggregationOps.push(o);
        } else if (['+', '-', '*', '/', '%', '^', '==', '!=', '>', '<', '>=', '<='].includes(opId)) {
            binaryOps.push(o);
        }
    }

    //Apply pipeline operations (format/parsers) - MUST BE FIRST
for (const o of formatOps) {
    const params = o.params || {};
    switch (o.id) {
        case 'json':
            expr += params.value ? ` | json ${params.value}` : ' | json';
            break;
        case 'logfmt':
            expr += params.value ? ` | logfmt ${params.value}` : ' | logfmt';
            break;
        case 'regexp':
            expr += ` | regexp \`${params.value || ''}\``;
            break;
        case 'pattern':
            expr += ` | pattern \`${params.value || ''}\``;
            break;
        case 'unpack':
            expr += ' | unpack';
            break;
        case 'decolorize':
            expr += ' | decolorize';
            break;
        case 'line_format':
            expr += ` | line_format \`${params.value || ''}\``;
            break;
        case 'label_format':
            // IMPROVED: Better handling of label_format syntax
            const formatValue = params.value || 'new_label="{{ .old_label }}"';
            expr += ` | label_format ${formatValue}`;
            break;
        case 'drop':
            // IMPROVED: Split comma-separated labels
            const dropLabels = params.value ? params.value.split(',').map(l => l.trim()).join(', ') : '';
            expr += dropLabels ? ` | drop ${dropLabels}` : ' | drop';
            break;
        case 'keep':
            // IMPROVED: Split comma-separated labels
            const keepLabels = params.value ? params.value.split(',').map(l => l.trim()).join(', ') : '';
            expr += keepLabels ? ` | keep ${keepLabels}` : ' | keep';
            break;
    }
}

    //Apply line filter operations
    for (const o of lineFilterOps) {
        const params = o.params || {};
        const value = params.value || '';
        switch (o.id) {
            case 'line_contains':
                expr += ` |= "${value}"`;
                break;
            case 'line_contains_not':
                expr += ` != "${value}"`;
                break;
            case 'line_contains_ci':
                expr += ` |~ "(?i)${value}"`;
                break;
            case 'line_contains_not_ci':
                expr += ` !~ "(?i)${value}"`;
                break;
            case 'line_matches_regex':
                expr += ` |~ "${value}"`;
                break;
            case 'line_matches_regex_not':
                expr += ` !~ "${value}"`;
                break;
            case 'line_filter_ip_matches':
                expr += ` |= ip(\`${value}\`)`;
                break;
        }
    }

    // Apply label filter operations
    for (const o of labelFilterOps) {
        const params = o.params || {};
        switch (o.id) {
            case 'label_filter':
                 const label = params.label || 'label_name';
                 const op = params.op || '=';
                 const value = params.value || '';
                 expr += ` | ${label} ${op} "${value}"`;
            break;
            case 'label_filter_ip_matches':
                const ipLabel = params.label || 'remote_addr';
                const ipOp = params.op || '=';
                const ipValue = params.value || '192.168.1.0/24';
                expr += ` | ${ipLabel} ${ipOp} ip(\`${ipValue}\`)`;
            break;
            case 'label_filter_no_errors':
                expr += ' | __error__=``';
                break;
        }
    }

    //Apply unwrap operations
    for (const o of unwrapOps) {
        const params = o.params || {};
        const label = params.value || params.label || 'responce_time';
        switch (o.id) {
            case 'unwrap':
                expr += ` | unwrap ${label}`;
                break;
            case 'unwrap_duration':
                expr += ` | unwrap duration(${label})`;
                break;
            case 'unwrap_bytes':
                expr += ` | unwrap bytes(${label})`;
                break;
        }
    }

    //Apply range functions (WRAP the expression)
    for (const o of rangeOps) {
        const params = o.params || {};
        const range = params.range || '$__auto';
        switch (o.id) {
            case 'rate':
            case 'rate_counter':
            case 'count_over_time':
            case 'sum_over_time':
            case 'avg_over_time':
            case 'min_over_time':
            case 'max_over_time':
            case 'first_over_time':
            case 'last_over_time':
            case 'stddev_over_time':
            case 'stdvar_over_time':
            case 'absent_over_time':
                expr = `${o.id}(${expr}[${range}])`;
                break;
            case 'quantile_over_time':
                expr = `quantile_over_time(${params.q || 0.5}, ${expr}[${range}])`;
                break;
            case 'bytes_rate':
                expr = `bytes_rate(${expr}[${range}])`;
                break;
            case 'bytes_over_time':
                expr = `bytes_over_time(${expr}[${range}])`;
                break;
        }
    }

    //Apply aggregation functions (WRAP the expression)
    for (const o of aggregationOps) {
        const params = o.params || {};
        const byClause = params.by && params.by.length > 0 ? ` by (${params.by.join(',')})` : '';
        
        switch (o.id) {
            case 'sum':
            case 'avg':
            case 'min':
            case 'max':
            case 'count':
            case 'stddev':
            case 'stdvar':
                expr = byClause ? `${o.id}${byClause}(${expr})` : `${o.id}(${expr})`;
                break;
            case 'topk':
            case 'bottomk':
                expr = `${o.id}(${params.k || 5}, ${expr})`;
                break;
        }
    }

    //Apply binary operations
    for (const o of binaryOps) {
        const params = o.params || {};
        const value = params.value || '1';
        expr = `${expr} ${o.id} ${value}`;
    }

    return expr;
}, [labels, lineFilters, ops]);

  // Notify parent of query changes (skip when rebuilt query matches saved expression)
  useEffect(() => {
    if (!onChange || !query || query === '{}') return;
    if (value && normalizeExpr(query) === normalizeExpr(value)) {
      return;
    }
    // Preserve manual/pasted expressions when visual builder cannot represent them cleanly
    if (
      value &&
      value.trim() &&
      !value.includes('{}') &&
      query.includes('{}')
    ) {
      return;
    }
    onChange(query);
  }, [query, onChange, value]);

  return (
    <div style={{ width: '100%' }}>
      {/* Left Panel - Builder */}
      <div style={{ flex: 1 }}>
        {/* Label Filters */}
        <Card 
          size="small" 
          title={
            <span style={{ fontWeight: 600 }}>
              Label Filters
              {loading && <span style={{ marginLeft: 8, color: '#1890ff', fontSize: 12, fontWeight: 400 }}>(Loading...)</span>}
              {!loading && labelKeys.length > 0 && <span style={{ marginLeft: 8, color: '#52c41a', fontSize: 12, fontWeight: 400 }}>({labelKeys.length} labels)</span>}
            </span>
          }
          extra={
            <Button 
              type="dashed" 
              size="small"onClick={addLabel}
            >
              Add
            </Button>
          }
          style={{ marginBottom: 12 }}
        >
          {labels.length === 0 ? (
            <div style={{ color: '#999', textAlign: 'center', padding: 8 }}>
              No label filters added
            </div>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              {labels.map((f, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="Label"
                    value={f.key || undefined}
                    onChange={(val) => updateLabel(i, 'key', val)}
                    style={{ width: 160,color: 'black',flexShrink: 1, minWidth: 0, maxWidth: '100%' }}
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                    dropdownStyle={{ minWidth: 200 }}
                    popupMatchSelectWidth={false}
                    className="loki-select"
                  >
                    {labelKeys.map(k => (
                      <Option key={k} value={k}>
                        <div title={k} style={{ wordBreak: 'break-all' }}>{k}</div>
                      </Option>
                    ))}
                  </Select>
                  
                  <Select
                    value={f.op}
                    onChange={(val) => updateLabel(i, 'op', val)}
                    style={{ width: 80,color: 'black',flexShrink: 1, minWidth: 0, maxWidth: '100%' }}
                    className="loki-select"
                  >
                    <Option value="=">=</Option>
                    <Option value="!=">!=</Option>
                    <Option value="=~">=~</Option>
                    <Option value="!~">!~</Option>
                  </Select>
                  
                  <Select
                    showSearch
                    allowClear
                    placeholder="Value"
                    value={f.value || undefined}
                    onChange={(val) => updateLabel(i, 'value', val)}
                    style={{ width: 400, color: 'black',overflow: 'hidden' }}
                    filterOption={(input, option) =>
                      option?.value?.toLowerCase().includes(input.toLowerCase())
                    }
                    dropdownStyle={{ minWidth: 300 }}
                    popupMatchSelectWidth={false}
                    className="loki-select"
                  >
                    {(valueOptions[f.key] || []).map(v => (
                      <Option key={v} value={v}>
                        <div title={v} style={{ wordBreak: 'break-all' }}>{v}</div>
                      </Option>
                    ))}
                  </Select>
                  
                  <Button
                    type="text"
                    dangeronClick={() => removeLabel(i)}
                  />
                </div>
              ))}
            </Space>
          )}
        </Card>

        {/* Line Contains */}
        <Card 
          size="small" 
          title={<span style={{ fontWeight: 600 }}>Line Contains</span>}
          extra={
            <Button 
              type="dashed" 
              size="small"onClick={addLine}
            >
              Add
            </Button>
          }
          style={{ marginBottom: 12 }}
        >
          {lineFilters.length === 0 ? (
            <div style={{ color: '#999', textAlign: 'center', padding: 8 }}>
              No line filters added — filter log content
            </div>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              {lineFilters.map((l, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Select
                    value={l.type}
                    onChange={(val) => updateLine(i, 'type', val)}
                    style={{ width: 100,color: 'black' }}
                    className="loki-select"
                  >
                    <Option value="|=">|= contains</Option>
                    <Option value="|~">|~ regex</Option>
                    <Option value="!=">!= not contains</Option>
                    <Option value="!~">!~ not regex</Option>
                  </Select>
                  
                  <Input
                    placeholder="text or regex pattern"
                    value={l.value}
                    onChange={(e) => updateLine(i, 'value', e.target.value)}
                    style={{ flex: 1 }}
                  />
                  
                  <Button
                    type="text"
                    dangeronClick={() => removeLine(i)}
                  />
                </div>
              ))}
            </Space>
          )}
        </Card>

        {/* Operations */}
        <Card 
          size="small" 
          title={<span style={{ fontWeight: 600, color: isDark ? '#e6edf3' : undefined }}>Operations</span>}
          extra={<OperationMenu mode='loki' onAdd={addOp} isDark={isDark} />}
          style={isDark ? { background: '#161b22', borderColor: '#30363d' } : undefined}
        >
          {ops.length === 0 ? (
            <div style={{ color: isDark ? '#8b949e' : '#999', textAlign: 'center', padding: 8 }}>
              No operations added — use +Operations
            </div>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              {ops.map((op, i) => (
                <OperationItem 
                  key={i} 
                  mode='loki' 
                  op={op} 
                  index={i} 
                  onRemove={removeOp} 
                  onChangeOpType={changeOpType} 
                  onUpdateParam={updateOpParam} 
                  labelKeys={labelKeys} 
                  rangeOptions={rangeOptions} 
                />
              ))}
            </Space>
          )}
        </Card>
      </div>

      {/* Right Panel - Query Preview (editable so user can paste custom LogQL) */}
      <div style={{ width: '100%', marginTop: 16 }}>
        <QueryPreview
          value={value || query || '{}'}
          onChange={onChange}
          placeholder="Paste or type LogQL (e.g. {job='varlogs'} |= 'error')"
        />
      </div>
    </div>
  );
}
